import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Spinner, EnvelopeSimple, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/host";

/** Premium sign-in form matching the DAAKYI mockup:
 *   • Mail / Lock icons inside pill inputs
 *   • Password eye toggle
 *   • "Keep me signed in on this device" checkbox (staff only)
 *   • Gold-accented primary Sign in button (thin gold line on top)
 *   • Optional OR + "Continue with single sign-on" outlined secondary button
 */
export function SignInForm({
  prefill,
  emailPlaceholder = "you@company.com",
  submitLabel = "Sign in",
  showSSO = true,
}) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (prefill) {
      setEmail(prefill.email);
      setPassword(prefill.password);
    }
  }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.mfaRequired) {
      // Send the user to the MFA challenge page with the challenge token in state.
      navigate("/mfa/challenge", {
        replace: true,
        state: { challengeToken: res.challengeToken, email: res.email },
      });
      return;
    }
    if (res.ok) {
      // Policy: if this user still needs to enrol MFA, force them through setup
      // before showing the workspace.
      if (res.mfaSetupRequired) navigate("/mfa/setup", { replace: true });
      else navigate(homeFor(res.user), { replace: true });
    } else {
      setError(res.error);
    }
  };

  const inputCls =
    "w-full rounded-full border border-input bg-white pl-11 pr-3 py-3.5 text-sm outline-none " +
    "placeholder:text-muted-foreground/60 focus:border-[#B79148] focus:ring-2 focus:ring-[#B79148]/25";

  return (
    <form onSubmit={submit} className="space-y-5">
      {/* Email */}
      <div>
        <label htmlFor="login-email" className="mb-2 block font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-[#0A0F2E]/70">
          Email
        </label>
        <div className="relative">
          <EnvelopeSimple className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B79148]" weight="regular" />
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={inputCls}
            placeholder={emailPlaceholder}
          />
        </div>
      </div>

      {/* Password + forgot */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor="login-password" className="block font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-[#0A0F2E]/70">
            Password
          </label>
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => navigate("/forgot-password")}
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#B79148] hover:text-[#96772C]"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B79148]" weight="regular" />
          <input
            id="login-password"
            data-testid="login-password-input"
            type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className={inputCls + " pr-11"}
            placeholder="••••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? "Hide password" : "Show password"}
            data-testid="login-password-toggle"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPw ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
          </button>
        </div>
      </div>

      {/* Keep me signed in — session persists via HTTP-only cookies; the checkbox is
          intentionally omitted until a differentiated shorter/longer session lifetime is wired. */}

      {error && (
        <div data-testid="login-error" role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {info && (
        <div data-testid="login-info" role="status" className="rounded-lg border border-[#B79148]/30 bg-[#B79148]/5 px-3 py-2 text-sm text-[#7A5B22]">
          {info}
        </div>
      )}

      {/* Primary submit with gold accent bar */}
      <div className="relative pt-1">
        <div className="absolute inset-x-4 top-0 h-[2px] rounded-full bg-[#B79148]" aria-hidden="true" />
        <button
          data-testid="login-submit-button"
          type="submit" disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A0F2E] px-5 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-50"
        >
          {busy
            ? <Spinner className="h-4 w-4 animate-spin" weight="bold" />
            : <>{submitLabel} <ArrowRight className="h-4 w-4" weight="bold" /></>}
        </button>
      </div>

      {/* OR divider + SSO */}
      {showSSO && (
        <>
          <div className="flex items-center gap-4 py-2" data-testid="sso-divider">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <button
            type="button"
            data-testid="sso-button"
            onClick={() => setInfo("Single sign-on is not configured for your organization yet. Please sign in with your DAAKYI email and password.")}
            className="w-full rounded-lg border border-[#0A0F2E]/25 bg-white px-5 py-3.5 text-sm font-semibold text-[#0A0F2E] transition-colors hover:border-[#0A0F2E]/60"
          >
            Continue with single sign-on
          </button>
        </>
      )}
    </form>
  );
}
